import React, { useState } from 'react';
import { Mail, Phone, MapPin, Send, Sparkles } from 'lucide-react';
import { useToast } from '../hooks/use-toast';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const ContactSection = () => {
  const { toast } = useToast();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    projectType: '',
    budget: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [focusedField, setFocusedField] = useState(null);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const response = await axios.post(`${BACKEND_URL}/api/contact`, formData);
      
      if (response.data.success) {
        toast({
          title: "Message Sent Successfully! 🎬",
          description: "We've received your inquiry and will get back to you within 24 hours.",
        });
        
        // Reset form
        setFormData({
          name: '',
          email: '',
          company: '',
          projectType: '',
          budget: '',
          message: ''
        });
      } else {
        toast({
          title: "Form Submitted",
          description: response.data.message || "Form submitted. Email service will be configured soon.",
          variant: "default"
        });
        
        // Still reset form on success submission to database
        setFormData({
          name: '',
          email: '',
          company: '',
          projectType: '',
          budget: '',
          message: ''
        });
      }
    } catch (error) {
      console.error('Contact form error:', error);
      toast({
        title: "Submission Error",
        description: "There was an error submitting your form. Please try again.",
        variant: "destructive"
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section id="contact" className="relative bg-black py-20 lg:py-32 overflow-hidden">
      {/* Futuristic Background */}
      <div className="absolute inset-0 futuristic-grid opacity-10" />
      <div className="absolute inset-0 scanlines" />
      
      {/* Animated Particles */}
      <div className="absolute inset-0">
        {[...Array(40)].map((_, i) => (
          <div
            key={i}
            className="absolute w-1 h-1 bg-red-600 rounded-full"
            style={{
              left: `${Math.random() * 100}%`,
              top: `${Math.random() * 100}%`,
              animation: `float ${5 + Math.random() * 10}s ease-in-out infinite`,
              animationDelay: `${Math.random() * 5}s`,
              opacity: Math.random() * 0.5 + 0.2
            }}
          />
        ))}
      </div>

      <div className="container mx-auto px-4 lg:px-8 relative z-10">
        <div className="grid lg:grid-cols-2 gap-16 max-w-7xl mx-auto">
          {/* Left Column - Info */}
          <div>
            <div className="relative inline-block mb-8">
              <Sparkles className="text-red-600 animate-pulse-glow absolute -top-4 -left-4" size={24} />
              <h2 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6 text-3d">
                Let's Create Something
                <br />
                <span className="holographic animate-gradient">Extraordinary</span>
              </h2>
            </div>
            <p className="text-lg text-white/70 leading-relaxed mb-12 relative">
              Whether you're a brand looking for a powerful commercial or an agency seeking a production partner, we're here to bring your vision to life.
              <div className="absolute -left-4 top-0 bottom-0 w-1 bg-gradient-to-b from-red-600 to-transparent" />
            </p>

            {/* Contact Info */}
            <div className="space-y-6">
              {[
                { icon: Mail, label: 'Email', value: 'contact@recfolk.com', href: 'mailto:contact@recfolk.com' },
                { icon: Phone, label: 'Phone', value: '+974 5999 7574', href: 'tel:+97459997574' },
                { icon: MapPin, label: 'Location', value: 'Doha, Qatar', href: null }
              ].map((item, index) => (
                <div 
                  key={index}
                  className="flex items-start gap-4 group"
                  style={{ animation: `fadeInUp 0.6s ease-out ${index * 150}ms both` }}
                >
                  <div className="relative">
                    <div className="glass-ultra p-4 rounded-2xl neon-red transform group-hover:scale-110 group-hover:rotate-6 transition-all duration-300">
                      <item.icon className="text-red-600" size={24} />
                    </div>
                    <div className="absolute inset-0 bg-red-600 blur-2xl opacity-50 group-hover:opacity-75 transition-opacity" />
                  </div>
                  <div>
                    <div className="text-white font-semibold mb-1 text-sm uppercase tracking-wider">{item.label}</div>
                    {item.href ? (
                      <a href={item.href} className="text-white/70 hover:text-red-600 transition-colors text-sm">
                        {item.value}
                      </a>
                    ) : (
                      <div className="text-white/70 text-sm whitespace-pre-line">{item.value}</div>
                    )}
                  </div>
                </div>
              ))}
            </div>

            {/* Stats */}
            <div className="mt-12 pt-12 border-t border-white/10 relative">
              <div className="absolute top-0 left-0 right-0 h-[2px] holographic" />
              <div className="grid grid-cols-2 gap-6">
                {[
                  { value: '24h', label: 'Response Time' },
                  { value: '100%', label: 'Client Satisfaction' }
                ].map((stat, index) => (
                  <div key={index} className="glass-card p-4 rounded-2xl hover-lift">
                    <div className="text-3xl md:text-4xl font-bold text-red-600 mb-1">{stat.value}</div>
                    <div className="text-white/60 text-sm uppercase tracking-wider">{stat.label}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Right Column - Form */}
          <div className="glass-card p-8 rounded-3xl border border-white/10 shadow-premium relative overflow-hidden">
            {/* Holographic Corner Accents */}
            <div className="absolute top-0 right-0 w-32 h-32 holographic opacity-10 rounded-bl-[100%]" />
            <div className="absolute bottom-0 left-0 w-32 h-32 holographic opacity-10 rounded-tr-[100%]" />

            <form onSubmit={handleSubmit} className="space-y-6 relative z-10">
              {/* Name */}
              <div className="relative">
                <label htmlFor="name" className="block text-white font-medium mb-2 text-sm uppercase tracking-wider">
                  Full Name *
                </label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  onFocus={() => setFocusedField('name')}
                  onBlur={() => setFocusedField(null)}
                  required
                  className="w-full glass-ultra border border-white/10 rounded-xl px-4 py-3 text-black placeholder-white/40 focus:outline-none focus:border-red-600 focus:ring-2 focus:ring-red-600/50 transition-all"
                  placeholder="John Doe"
                />
                {focusedField === 'name' && (
                  <div className="absolute bottom-0 left-0 right-0 h-[2px] holographic animate-shimmer" />
                )}
              </div>

              {/* Email */}
              <div className="relative">
                <label htmlFor="email" className="block text-white font-medium mb-2 text-sm uppercase tracking-wider">
                  Email Address *
                </label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  onFocus={() => setFocusedField('email')}
                  onBlur={() => setFocusedField(null)}
                  required
                  className="w-full glass-ultra border border-white/10 rounded-xl px-4 py-3 text-black placeholder-white/40 focus:outline-none focus:border-red-600 focus:ring-2 focus:ring-red-600/50 transition-all"
                  placeholder="john@company.com"
                />
                {focusedField === 'email' && (
                  <div className="absolute bottom-0 left-0 right-0 h-[2px] holographic animate-shimmer" />
                )}
              </div>

              {/* Company */}
              <div className="relative">
                <label htmlFor="company" className="block text-white font-medium mb-2 text-sm uppercase tracking-wider">
                  Company / Agency
                </label>
                <input
                  type="text"
                  id="company"
                  name="company"
                  value={formData.company}
                  onChange={handleChange}
                  onFocus={() => setFocusedField('company')}
                  onBlur={() => setFocusedField(null)}
                  className="w-full glass-ultra border border-white/10 rounded-xl px-4 py-3 text-black placeholder-white/40 focus:outline-none focus:border-red-600 focus:ring-2 focus:ring-red-600/50 transition-all"
                  placeholder="Your Company"
                />
                {focusedField === 'company' && (
                  <div className="absolute bottom-0 left-0 right-0 h-[2px] holographic animate-shimmer" />
                )}
              </div>

              {/* Project Type & Budget */}
              <div className="grid md:grid-cols-2 gap-6">
                <div className="relative">
                  <label htmlFor="projectType" className="block text-white font-medium mb-2 text-sm uppercase tracking-wider">
                    Project Type *
                  </label>
                  <select
                    id="projectType"
                    name="projectType"
                    value={formData.projectType}
                    onChange={handleChange}
                    onFocus={() => setFocusedField('projectType')}
                    onBlur={() => setFocusedField(null)}
                    required
                    className="w-full glass-ultra border border-white/10 rounded-xl px-4 py-3 text-black focus:outline-none focus:border-red-600 focus:ring-2 focus:ring-red-600/50 transition-all appearance-none"
                    style={{ backgroundImage: 'none' }}
                  >
                    <option value="" className="bg-black text-white">Select Type</option>
                    <option value="commercial" className="bg-black text-white">Commercial</option>
                    <option value="documentary" className="bg-black text-white">Documentary</option>
                    <option value="brand-content" className="bg-black text-white">Brand Content</option>
                    <option value="other" className="bg-black text-white">Other</option>
                  </select>
                  {focusedField === 'projectType' && (
                    <div className="absolute bottom-0 left-0 right-0 h-[2px] holographic animate-shimmer" />
                  )}
                </div>

                <div className="relative">
                  <label htmlFor="budget" className="block text-white font-medium mb-2 text-sm uppercase tracking-wider">
                    Budget Range
                  </label>
                  <select
                    id="budget"
                    name="budget"
                    value={formData.budget}
                    onChange={handleChange}
                    onFocus={() => setFocusedField('budget')}
                    onBlur={() => setFocusedField(null)}
                    className="w-full glass-ultra border border-white/10 rounded-xl px-4 py-3 text-black focus:outline-none focus:border-red-600 focus:ring-2 focus:ring-red-600/50 transition-all appearance-none"
                  >
                    <option value="" className="bg-black text-white">Select Budget</option>
                    <option value="10k-25k" className="bg-black text-white">$10k - $25k</option>
                    <option value="25k-50k" className="bg-black text-white">$25k - $50k</option>
                    <option value="50k-100k" className="bg-black text-white">$50k - $100k</option>
                    <option value="100k+" className="bg-black text-white">$100k+</option>
                  </select>
                  {focusedField === 'budget' && (
                    <div className="absolute bottom-0 left-0 right-0 h-[2px] holographic animate-shimmer" />
                  )}
                </div>
              </div>

              {/* Message */}
              <div className="relative">
                <label htmlFor="message" className="block text-white font-medium mb-2 text-sm uppercase tracking-wider">
                  Project Details *
                </label>
                <textarea
                  id="message"
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  onFocus={() => setFocusedField('message')}
                  onBlur={() => setFocusedField(null)}
                  required
                  rows={5}
                  className="w-full glass-ultra border border-white/10 rounded-xl px-4 py-3 text-black placeholder-white/40 focus:outline-none focus:border-red-600 focus:ring-2 focus:ring-red-600/50 transition-all resize-none"
                  placeholder="Tell us about your project..."
                />
                {focusedField === 'message' && (
                  <div className="absolute bottom-0 left-0 right-0 h-[2px] holographic animate-shimmer" />
                )}
              </div>

              {/* Submit Button */}
              <button
                type="submit"
                disabled={isSubmitting}
                className="group relative w-full bg-gradient-to-r from-red-600 to-red-500 text-white px-8 py-4 rounded-full overflow-hidden transition-all duration-300 text-sm font-bold tracking-wider uppercase neon-red hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-3"
              >
                {isSubmitting ? (
                  <>
                    <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                    <span className="relative z-10">Processing...</span>
                  </>
                ) : (
                  <>
                    <span className="relative z-10">Send Message</span>
                    <Send size={18} className="relative z-10" />
                  </>
                )}
                <div className="absolute inset-0 bg-gradient-to-r from-white/20 to-transparent translate-x-[-100%] group-hover:translate-x-[100%] transition-transform duration-700" />
              </button>
            </form>
          </div>
        </div>
      </div>

      {/* Decorative Orbs */}
      <div className="absolute bottom-0 -left-40 w-80 h-80 bg-red-600 rounded-full blur-[120px] opacity-10 animate-pulse-glow" />
      <div className="absolute top-0 -right-40 w-80 h-80 bg-white rounded-full blur-[120px] opacity-5 animate-pulse-glow" />
    </section>
  );
};

export default ContactSection;
